import { useLayoutEffect, useRef } from 'react';

// Whether the browser can move the head on the table's own scroll.
const TRACKS = typeof CSS !== 'undefined' && !!CSS.supports && CSS.supports('animation-timeline', 'scroll()');

/**
 * The List table's sideways scroll (PRD-1700): how far the head travels, which
 * edge fades show, and, where the browser has no scroll timeline, the head
 * mirrored to the rows. It also gives every cut-off `.list-table-cut` its full
 * text as `data-tip`, for the ResUI Tooltip: one line per cell, shown in full
 * in a tooltip, never a `title` (ResUI Design Decisions, Tables). It measures
 * on mount, on every scroll and resize, and after every render, because new rows, a new page or longer names change how
 * wide the content is without the scroller's own box changing size. Values are
 * only written when they change, so a measure costs no style work otherwise.
 */
export function useListTableScroll(rootRef, headRef, scrollRef, mounted) {
  const syncRef = useRef(null);

  useLayoutEffect(() => {
    const root = rootRef.current;
    const scroller = scrollRef.current;
    const head = headRef.current;
    if (!root || !scroller || !head) return undefined;
    const follow = (from, to) => { if (Math.abs(to.scrollLeft - from.scrollLeft) > 0.5) to.scrollLeft = from.scrollLeft; };
    const set = (name, value) => { if (root.dataset[name] !== value) root.dataset[name] = value; };
    const sync = () => {
      const max = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
      const m = `${max}px`;
      if (root.style.getPropertyValue('--list-table-max') !== m) root.style.setProperty('--list-table-max', m);
      if (!TRACKS) follow(scroller, head);
      set('fade', max - scroller.scrollLeft > 1 ? 'right' : 'none');
      set('scrolled', scroller.scrollLeft > 1 ? 'true' : 'false');
      root.querySelectorAll('.list-table-cut').forEach((el) => {
        const tip = el.scrollWidth > el.clientWidth + 1 ? el.textContent : null;
        if ((el.dataset.tip || null) !== tip) {
          if (tip) el.dataset.tip = tip;
          else delete el.dataset.tip;
        }
      });
    };
    // A wheel or trackpad over the head scrolls the table.
    const onHeadWheel = (e) => {
      const dx = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : (e.shiftKey ? e.deltaY : 0);
      if (!dx) return;
      scroller.scrollLeft += dx;
      e.preventDefault();
    };
    const onHeadScroll = () => follow(head, scroller);
    // With a scroll timeline the head clips rather than scrolls, so a header
    // reached from the keyboard brings the rows round to it.
    const onHeadFocus = (e) => {
      const cell = e.target.closest('.list-table-head > div');
      if (!cell || !cell.previousElementSibling) return;
      const view = head.getBoundingClientRect();
      const box = cell.getBoundingClientRect();
      const pin = parseFloat(root.style.getPropertyValue('--list-table-pin')) || 0;
      if (box.right > view.right) scroller.scrollLeft += box.right - view.right;
      else if (box.left < view.left + pin) scroller.scrollLeft -= view.left + pin - box.left;
    };
    scroller.addEventListener('scroll', sync, { passive: true });
    if (TRACKS) {
      head.addEventListener('wheel', onHeadWheel, { passive: false });
      head.addEventListener('focusin', onHeadFocus);
    } else head.addEventListener('scroll', onHeadScroll, { passive: true });
    let frame = 0;
    const observer = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => { cancelAnimationFrame(frame); frame = requestAnimationFrame(sync); })
      : null;
    observer?.observe(scroller);
    syncRef.current = sync;
    sync();
    return () => {
      syncRef.current = null;
      scroller.removeEventListener('scroll', sync);
      head.removeEventListener('wheel', onHeadWheel);
      head.removeEventListener('focusin', onHeadFocus);
      head.removeEventListener('scroll', onHeadScroll);
      observer?.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [rootRef, headRef, scrollRef, mounted]);

  // After every render: the content may have changed width.
  useLayoutEffect(() => { syncRef.current?.(); });
}
